/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import SpinningSpark from '../shared/components/SpinningSpark';
import adminFormStyles from '../shared/styles/adminForm.styles';
import TemplatesApi from './Templates.api';
import TemplateForm from './forms/TemplateForm';

const styles = {
  page: adminFormStyles.page,
  content: adminFormStyles.content,
  centered: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 320,
  },
  loadError: {
    ...adminFormStyles.section,
    marginX: 'auto',
    maxWidth: 560,
    p: 4,
    textAlign: 'center',
  },
  templatesButton: {
    borderRadius: '8px',
    textTransform: 'none',
  },
};

export default function TemplateFormPage() {
  const { template_id: templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertsContext);
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const isEditing = Boolean(templateId);
  const initialTemplate = isEditing ? location.state?.template ?? null : null;

  async function loadTopics() {
    try {
      const fetchedTopics = await TemplatesApi.getTopics();
      setTopics(fetchedTopics);
      return fetchedTopics;
    } catch (error) {
      console.error('Failed to load topics', error);
      showAlert('Failed to load topics. Please try again.', 'error');
      throw error;
    }
  }

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      setLoadFailed(false);

      try {
        await loadTopics();
        if (isEditing && !location.state?.template) {
          setLoadFailed(true);
        }
      } catch {
        setLoadFailed(true);
      } finally {
        setIsLoading(false);
      }
    }

    init();
    // Only re-run when the route id changes.
  }, [templateId]);

  function handleBack() {
    navigate('/templates');
  }

  function handleSaved() {
    navigate('/templates');
  }

  async function handleTopicsChanged() {
    await loadTopics();
  }

  function renderContent() {
    if (isLoading) {
      return (
        <Box sx={styles.centered}>
          <SpinningSpark height="50" />
        </Box>
      );
    }

    if (loadFailed || (isEditing && !initialTemplate)) {
      return (
        <Paper variant="outlined" sx={styles.loadError}>
          <Stack spacing={2} alignItems="center">
            <Typography component="h1" variant="h5">
              Template not found
            </Typography>
            <Button
              type="button"
              variant="contained"
              onClick={handleBack}
              sx={styles.templatesButton}
            >
              Back to templates
            </Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <TemplateForm
        initialTemplate={initialTemplate}
        topics={topics}
        onBack={handleBack}
        onSaved={handleSaved}
        onTopicsChanged={handleTopicsChanged}
      />
    );
  }

  return (
    <Box sx={styles.page}>
      <Box sx={styles.content}>{renderContent()}</Box>
    </Box>
  );
}
